import { useEffect, useState } from "react";

import {
  ArrowButton,
  SidebarNavigationContainer,
  SidebarNavigationItem,
  Typography,
  IconWrapper,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as FaceManIcon } from "@king-ict/eupisi-ui/components/Icon/FaceMan.svg";
import { KindergartenRoleEnum } from "@king-ict/eupisi-ui/models";
import { isEmpty } from "@king-ict/eupisi-ui/utils";
import qs from "qs";
import { useTranslation } from "react-i18next";
import { useRouteMatch } from "react-router";
import { useHistory, useParams, useLocation } from "react-router";

import { getRequestBasicInfo } from "../../api/kindergartenApi";
import { useTypedSelector } from "../../app/rootReducer";
import EnrollmentTypeEnum from "../../models/EnrollmentType/EnrollmentTypeEnum";
import RequestBasicInfo from "../../models/Request/RequestBasicInfo";
import RequestStatusEnum from "../../models/Request/RequestStatusEnum";
import {
  adminRequestsUrl,
  adminSubsequentRequestsUrl,
  dashboard as dashboardRoute,
  draftPreschoolRequestRoute,
  editPreschoolRequestRoute,
  adminPreschoolRequestsUrl,
} from "../../routes/routes";
import BenefitPage from "./Benefits/BenefitPage";
import EvaluationResultPage from "./EvaluationResult/EvaluationResultPage";
import PhysicalExaminationPage from "./PhysicalExamination/PhysicalExaminationPage";
import RequestDetailsPage from "./RequestDetails/RequestDetailsPage";
import styles from "./RequestInfoLayout.module.scss";

export default function RequestInfoLayout() {
  const [requestBasicInfo, setRequestBasicInfo] = useState(
    {} as RequestBasicInfo
  );

  const user = useTypedSelector((state) => state.application.user);

  const pedagogicalYear = useTypedSelector((state) => state.pedagogicalYear);

  const draftPreschoolRequestMatch = useRouteMatch(
    draftPreschoolRequestRoute()
  );
  const editPreschoolRequestMatch = useRouteMatch(editPreschoolRequestRoute());

  const { t } = useTranslation();

  const location = useLocation();
  const params = qs.parse(location.search.substring(1));
  const { applicantParentOib } = params;

  const { reqId } = useParams<{ reqId: string; guid: string }>();

  const history = useHistory();

  const handleBackToDashboard = () => {
    if (user.roleId === KindergartenRoleEnum.Parent) {
      history.push(dashboardRoute());
    } else {
      window.location.href =
        editPreschoolRequestMatch != null || draftPreschoolRequestMatch != null
          ? adminPreschoolRequestsUrl()
          : pedagogicalYear.currentImportantDate.enrollmentTypeId ===
            EnrollmentTypeEnum.SubsequentEnrollment
          ? adminSubsequentRequestsUrl()
          : adminRequestsUrl();
    }
  };

  useEffect(() => {
    (async () => {
      let reqBasicInfo: RequestBasicInfo;
      if (reqId) {
        reqBasicInfo = await getRequestBasicInfo(+reqId);
        setRequestBasicInfo(reqBasicInfo);
      }
    })();
  }, [reqId]);

  const handleSetRequestBasicInfo = (requestBasicInfo: RequestBasicInfo) => {
    setRequestBasicInfo(requestBasicInfo);
  };

  const sidebarTitle = () => {
    return (
      <>
        <div className={styles.subHeader}>
          <div>
            <IconWrapper color="body-color">
              <FaceManIcon aria-label={t("accessibility.childIcon")} />
            </IconWrapper>
            <span className={styles.nameTitle}>
              {requestBasicInfo && requestBasicInfo.childName}
            </span>
          </div>
        </div>
      </>
    );
  };

  const getPages = () => {
    if (
      !isEmpty(requestBasicInfo) &&
      requestBasicInfo.statusId !== RequestStatusEnum.Draft
    ) {
      return [
        <SidebarNavigationItem
          name={t("requestDetails.results")}
          route={`results`}
          key={`results`}
        >
          <EvaluationResultPage requestBasicInfo={requestBasicInfo} />
        </SidebarNavigationItem>,

        <SidebarNavigationItem
          name={t("requestDetails.physicalExamination")}
          route={`physical-examination`}
          key={`physical-examination`}
        >
          <PhysicalExaminationPage requestBasicInfo={requestBasicInfo} />
        </SidebarNavigationItem>,
        requestBasicInfo.statusId === RequestStatusEnum.Finalized ? (
          <SidebarNavigationItem
            name={t("requestDetails.benefits")}
            route={`benefits`}
            key={`benefits`}
          >
            <BenefitPage requestBasicInfo={requestBasicInfo}></BenefitPage>
          </SidebarNavigationItem>
        ) : null,
      ];
    }
  };

  return (
    <>
      {requestBasicInfo && (
        <>
          <div className={styles.layoutHeader}>
            <div>
              <ArrowButton
                ariaLabel={t("accessibility.backButton")}
                backwards
                onClick={handleBackToDashboard}
              ></ArrowButton>
            </div>
            <Typography
              variant="base"
              color="primary"
              className={styles.buttonText}
            >
              {user.roleId === KindergartenRoleEnum.Parent
                ? t("requestDetails.backToDashboard")
                : t("requestDetails.backToAdminRequestList")}
            </Typography>
          </div>

          <SidebarNavigationContainer
            color="primary"
            customTitle={sidebarTitle()}
          >
            <SidebarNavigationItem
              name={t("requestDetails.details")}
              route={`details`}
              url={
                user.roleId !== KindergartenRoleEnum.Parent &&
                applicantParentOib
                  ? `${location.pathname}?applicantParentOib=${applicantParentOib}`
                  : undefined
              }
              selectedByRoute={true}
              key={`details`}
            >
              <RequestDetailsPage
                requestBasicInfo={requestBasicInfo}
                history={history}
                onGetDraft={handleSetRequestBasicInfo}
              />
            </SidebarNavigationItem>
            {getPages()}
          </SidebarNavigationContainer>
        </>
      )}
    </>
  );
}
